import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, History, Loader2, RefreshCw, Trophy } from 'lucide-react';
import api from 'lib/api';
import { API_ENDPOINTS } from 'constants/game';
import { HandHistoryAction, HandHistoryEntry } from 'types/game';
import PageHero from 'components/PageHero';
import { formatCurrencyAmount } from 'lib/currency-format';
import { useAuth } from 'context/AuthProvider';
import { displayHandCategory } from 'lib/hand-category';

const RESULT_STYLES: Record<HandHistoryEntry['result'], string> = {
  win: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  loss: 'bg-red-500/10 text-red-300 border-red-500/25',
  fold: 'bg-white/5 text-white/50 border-white/10',
};

function formatYourHand(hand: HandHistoryEntry): string {
  if (hand.handCategory) return displayHandCategory(hand.handCategory) ?? hand.handCategory;
  const codes = hand.cards
    .map((c) => c.code)
    .filter((c) => c && c !== 'back');
  if (codes.length) return codes.join(' · ');
  return '—';
}

function formatWhen(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function HandHistory() {
  const { user } = useAuth();
  const [hands, setHands] = useState<HandHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actionsByBattle, setActionsByBattle] = useState<Record<number, HandHistoryAction[]>>({});
  const [actionsLoading, setActionsLoading] = useState<number | null>(null);

  const currencySymbol = user?.currencySymbol ?? '';
  const currencyCode = user?.currency ?? null;

  const fetchHands = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError('');
    try {
      const res = await api.get<HandHistoryEntry[]>(`${API_ENDPOINTS.FRONTEND.HAND_HISTORY}?limit=10`);
      if (res.success && Array.isArray(res.data)) {
        setHands(res.data);
      } else {
        setHands([]);
        setError(res.message || 'Could not load hand history');
      }
    } catch {
      setHands([]);
      setError('Could not load hand history — try again');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchHands();
  }, [fetchHands]);

  const toggleExpand = async (battleId: number) => {
    if (expandedId === battleId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(battleId);
    if (actionsByBattle[battleId]) return;

    setActionsLoading(battleId);
    try {
      const res = await api.get<HandHistoryAction[]>(
        API_ENDPOINTS.FRONTEND.HAND_HISTORY_ACTIONS(battleId)
      );
      if (res.success && Array.isArray(res.data)) {
        setActionsByBattle((prev) => ({ ...prev, [battleId]: res.data! }));
      }
    } finally {
      setActionsLoading(null);
    }
  };

  return (
    <div className="pb-8">
      <PageHero
        eyebrow="Game history"
        title="Last 10 hands"
        showBack
        description="Review your recent Teen Patti hands — result, pot, and betting actions per round."
        icon={<History size={16} className="text-tp-gold" />}
        aside={
          <button
            type="button"
            onClick={() => void fetchHands(true)}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/10 disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        }
      />

      {error && (
        <p className="mt-4 rounded-lg border border-red-500/30 bg-red-950/40 px-4 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {loading ? (
        <div className="mt-8 flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-tp-gold/70" />
        </div>
      ) : hands.length === 0 ? (
        <div className="game-table-card mt-8 p-10 text-center text-white/55">
          No completed hands yet — play a round and your history will appear here.
        </div>
      ) : (
        <ul className="mt-8 space-y-3">
          {hands.map((hand) => {
            const expanded = expandedId === hand.battleId;
            const actions = actionsByBattle[hand.battleId] ?? [];
            return (
              <li key={hand.battleId} className="game-table-card overflow-hidden">
                <button
                  type="button"
                  className="flex w-full items-start justify-between gap-4 p-5 text-left transition hover:bg-white/[0.02]"
                  onClick={() => void toggleExpand(hand.battleId)}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-display text-lg font-bold text-white">{hand.tableName}</h3>
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${RESULT_STYLES[hand.result]}`}
                      >
                        {hand.result}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-white/45">{formatWhen(hand.endedAt || hand.createdAt)}</p>
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Pot</dt>
                        <dd className="font-semibold tabular-nums text-tp-gold">
                          {formatCurrencyAmount(currencySymbol, hand.potAmount, currencyCode)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Boot</dt>
                        <dd className="font-semibold tabular-nums text-white/75">
                          {formatCurrencyAmount(currencySymbol, hand.bootAmount, currencyCode)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Winner</dt>
                        <dd className="truncate font-semibold text-white/75">
                          {hand.winnerUsername || '—'}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-[10px] font-bold uppercase tracking-wider text-white/35">Your hand</dt>
                        <dd className="font-semibold text-white/75">
                          {formatYourHand(hand)}
                        </dd>
                      </div>
                    </dl>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-white/40">
                    {hand.result === 'win' && <Trophy size={18} className="text-tp-gold" />}
                    {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </button>

                {expanded && (
                  <div className="border-t border-white/10 bg-black/30 px-5 py-4">
                    {actionsLoading === hand.battleId ? (
                      <div className="flex justify-center py-6">
                        <Loader2 size={22} className="animate-spin text-tp-gold/60" />
                      </div>
                    ) : actions.length === 0 ? (
                      <p className="text-sm text-white/45">No action log recorded for this hand.</p>
                    ) : (
                      <ol className="max-h-64 space-y-2 overflow-y-auto text-sm">
                        {actions.map((action) => (
                          <li
                            key={action.id}
                            className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.03] px-3 py-2"
                          >
                            <div className="min-w-0">
                              <span className="text-[10px] font-bold uppercase text-white/35">
                                R{action.round_number}
                              </span>
                              <span className="ml-2 font-medium text-white/80">{action.username}</span>
                              <span className="ml-2 text-xs uppercase text-emerald-300/80">
                                {(action as HandHistoryAction & { action_label?: string }).action_label ||
                                  action.action_code}
                              </span>
                            </div>
                            {Number(action.amount) > 0 && (
                              <span className="shrink-0 tabular-nums font-semibold text-tp-gold/90">
                                {formatCurrencyAmount(currencySymbol, Number(action.amount), currencyCode)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
